/**
 * Hooks for statistics data fetching with React Query
 */

import { useQuery } from '@tanstack/react-query';
import { statisticsService } from '../services/statistics';
import type { StatisticsParams } from '../types';

/**
 * Query keys for statistics
 */
export const statisticsKeys = {
  all: ['statistics'] as const,
  campaign: (id: number, params?: StatisticsParams) =>
    [...statisticsKeys.all, 'campaign', id, params] as const,
};

/**
 * Fetch statistics for a campaign
 * @param campaignId - Campaign ID
 * @param params - Statistics query parameters (date range, period)
 * @param enabled - Whether the query should run (default: true)
 * @returns Query result with campaign statistics
 *
 * @example
 * ```tsx
 * const { data: stats, isLoading } = useCampaignStatistics(
 *   campaignId,
 *   { period: 'last_30_days' }
 * );
 *
 * if (isLoading) return <Spinner />;
 *
 * return (
 *   <div>
 *     <h2>Total Requests: {stats.summary.total_requests}</h2>
 *     <PieChart data={[
 *       { name: 'Human', value: stats.summary.human_count },
 *       { name: 'Bot', value: stats.summary.bot_count }
 *     ]} />
 *   </div>
 * );
 * ```
 */
export function useCampaignStatistics(
  campaignId: number,
  params?: StatisticsParams,
  enabled = true
) {
  return useQuery({
    queryKey: statisticsKeys.campaign(campaignId, params),
    queryFn: () => statisticsService.getCampaignStatistics(campaignId, params),
    enabled: enabled && campaignId > 0,
    staleTime: 1000 * 60 * 2, // 2 minutes (stats change frequently)
    refetchInterval: 1000 * 60 * 5, // Auto-refetch every 5 minutes
  });
}

export default useCampaignStatistics;
